/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { action, observable } from "mobx";
import CommonPopup from "Components/common/CommonPopup";
import CheckBox from "Components/common/CheckBox";
import makeApi, { URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import { ReactComponent as EnrichEmails } from "Assets/svg/Enrich/enrichEmails.svg";
import { ReactComponent as EnrichPhone } from "Assets/svg/Enrich/enrichPhone.svg";
import { ReactComponent as EnrichIcon } from "Assets/svg/Enrich/enrich.svg";
import "./enrichPopup.scss";

/**
 * Lets other parts of the Contacts page (e.g. the post-import prompt) open the
 * Enrich popup for an explicit set of contact ids. ContactModule renders it.
 */
class EnrichPromptState {
  @observable show = false;
  @observable contactIds = [];

  @action
  open = (contactIds = []) => {
    this.contactIds = contactIds;
    this.show = contactIds.length > 0;
  };

  @action
  close = () => {
    this.show = false;
    this.contactIds = [];
  };
}

export const enrichPromptState = new EnrichPromptState();

const OPTIONS_STORAGE_KEY = "sg_enrich_popup_options";
const DEFAULT_OPTIONS = { profile: false, email: true, phone: false };

const readSavedOptions = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(OPTIONS_STORAGE_KEY));
    return saved && typeof saved === "object"
      ? { ...DEFAULT_OPTIONS, ...saved }
      : { ...DEFAULT_OPTIONS };
  } catch (e) {
    return { ...DEFAULT_OPTIONS };
  }
};

const saveOptions = options => {
  try {
    localStorage.setItem(OPTIONS_STORAGE_KEY, JSON.stringify(options));
  } catch (e) {
    // localStorage may be unavailable (private mode); the popup still works
  }
};

// Maps the checkbox selection onto the bulk-op payload the backend expects.
export const buildEnrichContactPayload = ({ profile, email, phone }) => {
  let enrichType = "PROFILE";
  if (email && phone) enrichType = "EMAIL_PHONE";
  else if (email) enrichType = "EMAIL";
  else if (phone) enrichType = "PHONE";
  return { enrichType, profile: Boolean(profile) };
};

const fmt = n => Utils.numberWithCommas(n || 0);

/**
 * Enrich popup: one checkbox per step (profile / emails / phones) with per-step
 * counts and a credit estimate, then a single "Enrich" that runs them in order.
 *
 * props:
 *  - selectedCount: number of contacts the user selected
 *  - getPreviewPayload(): the same {search, filter, totalCount} payload the run uses
 *  - onSubmit(enrichContactPayload): starts the run
 *  - onClose()
 */
const EnrichPopup = props => {
  const {
    selectedCount = 0,
    getPreviewPayload = () => ({}),
    onSubmit = () => {},
    onClose = () => {}
  } = props || {};

  const [options, setOptions] = useState(readSavedOptions);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchPreview = async () => {
    setLoading(true);
    const config = {
      url: URLS.enrichPreview,
      method: "POST",
      data: getPreviewPayload()
    };
    const response = await makeApi(config);
    setPreview(response?.data || null);
    setLoading(false);
  };

  useEffect(() => {
    fetchPreview();
  }, []);

  const toggle = (checked, id) => {
    const next = { ...options, [id]: checked };
    setOptions(next);
    saveOptions(next);
  };

  const {
    total = selectedCount,
    missingEmail = 0,
    missingPhone = 0,
    profileEligible = 0,
    remainingCredits = 0,
    creditsPerEmail = 1,
    creditsPerPhone = 10,
    phoneEnabled = true,
    bulkEnrichLimit = 0,
    truncated = false
  } = preview || {};

  // One credit pool: profile and email cost creditsPerEmail each, phone creditsPerPhone.
  const profileCredits = profileEligible * creditsPerEmail;
  const emailCredits = missingEmail * creditsPerEmail;
  const phoneCredits = missingPhone * creditsPerPhone;
  const creditsNeeded =
    (options.profile ? profileCredits : 0) +
    (options.email ? emailCredits : 0) +
    (options.phone && phoneEnabled ? phoneCredits : 0);
  const nothingSelected =
    !options.profile && !options.email && !(options.phone && phoneEnabled);
  const overLimit = truncated || (bulkEnrichLimit > 0 && selectedCount > bulkEnrichLimit);
  const notEnoughCredits = creditsNeeded > remainingCredits;
  const canSubmit =
    !loading &&
    !submitting &&
    !nothingSelected &&
    !overLimit &&
    !notEnoughCredits;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(
        buildEnrichContactPayload({
          ...options,
          phone: options.phone && phoneEnabled
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const rows = [
    {
      id: "profile",
      icon: <EnrichIcon />,
      title: "Update profile",
      description:
        "Title, seniority, department, location, LinkedIn and company. Fills empty fields only.",
      count: `${fmt(profileEligible)} of ${fmt(total)} can be looked up`,
      credits: `up to ${fmt(profileCredits)} ${
        profileCredits === 1 ? "credit" : "credits"
      }`
    },
    {
      id: "email",
      icon: <EnrichEmails />,
      title: "Find missing emails",
      description: "Finds a verified work email for contacts that have none.",
      count: `${fmt(missingEmail)} of ${fmt(total)} have no email`,
      credits: `up to ${fmt(emailCredits)} ${
        emailCredits === 1 ? "credit" : "credits"
      }`
    },
    {
      id: "phone",
      icon: <EnrichPhone />,
      title: "Find missing phone numbers",
      description: phoneEnabled
        ? `Finds a mobile or direct number for contacts that have none. ${creditsPerPhone} credits per number found.`
        : "Phone numbers aren't included in your plan.",
      count: `${fmt(missingPhone)} of ${fmt(total)} have no phone`,
      credits: `up to ${fmt(phoneCredits)} ${
        phoneCredits === 1 ? "credit" : "credits"
      }`,
      disabled: !phoneEnabled
    }
  ];

  const getBlockingMessage = () => {
    if (loading) return "";
    if (overLimit) {
      return `Up to ${fmt(bulkEnrichLimit)} contacts per run. Narrow your selection.`;
    }
    if (notEnoughCredits) {
      return `Needs up to ${fmt(creditsNeeded)}, you have ${fmt(remainingCredits)}.`;
    }
    return "";
  };
  const blockingMessage = getBlockingMessage();

  return (
    <CommonPopup
      className="enrichPopup"
      headerTitle={`Enrich ${fmt(selectedCount)} ${
        selectedCount === 1 ? "contact" : "contacts"
      }`}
      closeIconCbk={onClose}
    >
      <div className="enrichPopupBody">
        <p className="enrichPopupHint">
          Choose what to enrich. Steps run in this order: profile, then emails
          and phone numbers. Existing values are never overwritten.
        </p>
        <div className="enrichOptions">
          {rows.map(row => (
            <div
              key={row.id}
              className={`enrichOption ${
                options[row.id] && !row.disabled ? "selected" : ""
              } ${row.disabled ? "disabled" : ""}`}
            >
              <CheckBox
                id={row.id}
                checked={Boolean(options[row.id]) && !row.disabled}
                disabled={row.disabled}
                cbk={(checked, id) => !row.disabled && toggle(checked, id)}
              />
              {/* The rest of the row toggles too; CheckBox handles its own click. */}
              <div
                className="enrichOptionMain"
                onClick={() => !row.disabled && toggle(!options[row.id], row.id)}
              >
                <span className="enrichOptionIcon">{row.icon}</span>
                <span className="enrichOptionText">
                  <span className="enrichOptionTitle">{row.title}</span>
                  <span className="enrichOptionDesc">{row.description}</span>
                  <span className="enrichOptionMeta">
                    {loading ? "Counting…" : row.count}
                  </span>
                </span>
                {!loading && (
                  <span className="enrichOptionCredits">{row.credits}</span>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className={`enrichFooter ${blockingMessage ? "hasError" : ""}`}>
          <div className="enrichFooterInfo">
            {loading ? (
              <span className="enrichFooterPrimary muted">Calculating credits…</span>
            ) : blockingMessage ? (
              <>
                <span className="enrichFooterPrimary error">
                  <i className="material-icons-outlined">error_outline</i>
                  {overLimit ? "Selection too large" : "Not enough credits"}
                </span>
                <span className="enrichFooterSecondary">{blockingMessage}</span>
              </>
            ) : (
              <>
                <span className="enrichFooterPrimary">
                  Uses up to {fmt(creditsNeeded)}{" "}
                  {creditsNeeded === 1 ? "credit" : "credits"}
                </span>
                <span className="enrichFooterSecondary">
                  {fmt(remainingCredits)} available · charged only for what's found
                </span>
              </>
            )}
          </div>
          <div className="enrichFooterActions">
            <button
              type="button"
              className="enrichCancelBtn"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`enrichSubmitBtn ${submitting ? "loading" : ""}`}
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting
                ? "Starting…"
                : `Enrich ${fmt(selectedCount)} ${
                    selectedCount === 1 ? "contact" : "contacts"
                  }`}
            </button>
          </div>
        </div>
      </div>
    </CommonPopup>
  );
};

export { EnrichPopup };
export default observer(EnrichPopup);
